export default [
  // Legacy / mistaken URLs (e.g. /admin/dashboard from production-style bookmarks)
  { path: '/admin', redirect: '/' },
  { path: '/admin/dashboard', redirect: '/' },
  {
    path: '/admin/:pathMatch(.*)*',
    redirect: (to) => {
      const tail = to.params.pathMatch;
      if (!tail || (Array.isArray(tail) && tail.length === 0)) {
        return '/';
      }
      const segments = Array.isArray(tail) ? tail : [tail];
      return `/${segments.join('/')}`;
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/auth/LoginPage.vue"),
    meta: {
      title: "Login",
      guest: true,
    },
  },
  {
    path: "/maintenance",
    name: "maintenance",
    component: () => import("@/views/pages/MaintenancePage.vue"),
    meta: {
      title: "Maintenance",
    },
  },
  {
    path: "/",
    name: "admin",
    component: () => import("@/layouts/main.vue"),
    meta: { requiresAuth: true },
    children: [
      // Dashboard
      {
        path: "",
        name: "dashboard",
        component: () => import("@/views/dashboard/DashboardPage.vue"),
        meta: { title: "Dashboard" },
      },

      // ── Settings Module ──────────────────
      {
        path: "settings",
        name: "settings-index",
        component: () => import("@/views/settings/SettingsIndex.vue"),
        meta: { title: "Settings" },
      },
      {
        path: "settings/countries",
        name: "settings-countries",
        component: () => import("@/views/settings/CountriesPage.vue"),
        meta: { title: "Countries" },
      },
      {
        path: "settings/cities",
        name: "settings-cities",
        component: () => import("@/views/settings/CitiesPage.vue"),
        meta: { title: "Cities" },
      },
      {
        path: "settings/currencies",
        name: "settings-currencies",
        component: () => import("@/views/settings/CurrenciesPage.vue"),
        meta: { title: "Currencies" },
      },
      {
        path: "settings/exchange-rates",
        name: "settings-exchange-rates",
        component: () => import("@/views/settings/ExchangeRatesPage.vue"),
        meta: { title: "Exchange Rates" },
      },
      {
        path: "settings/pages",
        name: "settings-pages",
        component: () => import("@/views/settings/PageSettings.vue"),
        meta: { title: "Terms & Policies" },
      },
      {
        path: "settings/notifications",
        name: "settings-notifications",
        component: () => import("@/views/settings/NotificationCampaignPage.vue"),
        meta: { title: "Push Notifications" },
      },
      {
        path: "settings/notifications/history",
        name: "settings-notifications-history",
        component: () => import("@/views/settings/NotificationCampaignsList.vue"),
        meta: { title: "Campaigns History" },
      },
      {
        path: "settings/notifications/campaigns/:id",
        name: "settings-notifications-detail",
        component: () => import("@/views/settings/NotificationCampaignDetail.vue"),
        meta: { title: "Campaign Details & Stats" },
      },
      {
        path: "settings/roles",
        name: "settings-roles",
        component: () => import("@/views/settings/RolesIndex.vue"),
        meta: { title: "Roles & Permissions" },
      },
      {
        path: "settings/faqs",
        name: "settings-faqs",
        component: () => import("@/views/settings/FaqsPage.vue"),
        meta: { title: "FAQs" },
      },
      {
        path: "settings/attributes",
        name: "settings-attributes",
        component: () => import("@/views/settings/AttributesPage.vue"),
        meta: { title: "Attributes" },
      },
      {
        path: "settings/colors",
        name: "settings-colors",
        component: () => import("@/views/settings/ColorsPage.vue"),
        meta: { title: "Colors" },
      },

      // ── Users Module ─────────────────────
      {
        path: "users",
        name: "users-index",
        component: () => import("@/views/users/UsersIndex.vue"),
        meta: { title: "Users" },
      },
      {
        path: "users/create",
        name: "users-create",
        component: () => import("@/views/users/UserCreate.vue"),
        meta: { title: "Create User" },
      },
      {
        path: "users/:id/edit",
        name: "users-edit",
        component: () => import("@/views/users/UserEdit.vue"),
        meta: { title: "Edit User" },
      },
      {
        path: "users/:id/transactions",
        name: "users-transactions",
        component: () => import("@/views/users/UserTransactions.vue"),
        meta: { title: "User Transactions" },
      },
      {
        path: "users/:id/orders",
        name: "users-orders",
        component: () => import("@/views/users/UserOrders.vue"),
        meta: { title: "User Orders" },
      },
      {
        path: "users/:id",
        name: "users-show",
        component: () => import("@/views/users/UserShow.vue"),
        meta: { title: "User Details" },
      },
      {
        path: "loyalty",
        name: "loyalty-dashboard",
        component: () => import("@/views/loyalty/LoyaltyDashboard.vue"),
        meta: { title: "Loyalty" },
      },
      {
        path: "loyalty-tiers",
        name: "loyalty-tiers",
        component: () => import("@/views/users/LoyaltyTiers.vue"),
        meta: { title: "Loyalty Tiers" },
      },
      {
        path: "loyalty/point-rules",
        name: "loyalty-point-rules",
        component: () => import("@/views/loyalty/LoyaltyPointRules.vue"),
        meta: { title: "Loyalty Point Rules" },
      },
      {
        path: "loyalty/merchant-rewards",
        name: "loyalty-merchant-rewards",
        component: () => import("@/views/loyalty/MerchantRewards.vue"),
        meta: { title: "Merchant Rewards" },
      },
      {
        path: "loyalty/transactions",
        name: "loyalty-transactions",
        component: () => import("@/views/loyalty/LoyaltyTransactions.vue"),
        meta: { title: "Loyalty Transactions" },
      },

      // ── Delivery Module ──────────────────
      {
        path: "delivery",
        name: "delivery-dashboard",
        component: () => import("@/views/delivery/DeliveryDashboard.vue"),
        meta: { title: "Delivery" },
      },
      {
        path: "delivery/vehicle-types",
        name: "delivery-vehicle-types",
        component: () => import("@/views/delivery/VehicleTypes.vue"),
        meta: { title: "Vehicle Types" },
      },
      {
        path: "delivery/services/:service",
        name: "delivery-service",
        component: () => import("@/views/delivery/ServiceShow.vue"),
        meta: { title: "Service Details" },
      },
      {
        path: "delivery/boys",
        name: "delivery-boys",
        component: () => import("@/views/delivery/DeliveryBoysIndex.vue"),
        meta: { title: "Delivery Boys" },
      },
      {
        path: "delivery/boys/create",
        name: "delivery-boys-create",
        component: () => import("@/views/delivery/DeliveryBoyCreate.vue"),
        meta: { title: "Create Delivery Boy" },
      },
      {
        path: "delivery/boys/:id",
        name: "delivery-boys-show",
        component: () => import("@/views/delivery/DeliveryBoyShow.vue"),
        meta: { title: "Delivery Boy Details" },
      },
      {
        path: "delivery/boys/:id/edit",
        name: "delivery-boys-edit",
        component: () => import("@/views/delivery/DeliveryBoyEdit.vue"),
        meta: { title: "Edit Delivery Boy" },
      },
      {
        path: "delivery/sos",
        name: "delivery-sos",
        component: () => import("@/views/delivery/DeliverySosIndex.vue"),
        meta: { title: "SOS Alerts" },
      },
      {
        path: "delivery/withdrawals",
        name: "delivery-withdrawals",
        component: () => import("@/views/delivery/DriverWithdrawalsIndex.vue"),
        meta: { title: "Driver Withdrawals" },
      },
      {
        path: "delivery/order-declines",
        name: "delivery-order-declines",
        component: () => import("@/views/delivery/DriverOrderDeclinesIndex.vue"),
        meta: { title: "Order Declines" },
      },

      // ── Orders Module ────────────────────
      {
        path: "orders",
        name: "orders-index",
        component: () => import("@/views/orders/OrdersIndex.vue"),
        meta: { title: "Orders" },
      },
      {
        path: "orders/create",
        name: "orders-create",
        component: () => import("@/views/orders/OrderCreate.vue"),
        meta: { title: "Create Order" },
      },
      {
        path: "orders/:id",
        name: "orders-show",
        component: () => import("@/views/orders/OrderShow.vue"),
        meta: { title: "Order Details" },
      },

      // ── Returns Module ───────────────────
      {
        path: "returns",
        name: "returns-dashboard",
        component: () => import("@/views/returns/ReturnsDashboard.vue"),
        meta: { title: "Returns" },
      },
      {
        path: "returns/requests",
        name: "returns-requests",
        component: () => import("@/views/returns/ReturnRequestsIndex.vue"),
        meta: { title: "Return Requests" },
      },
      {
        path: "returns/requests/:id",
        name: "returns-request-show",
        component: () => import("@/views/returns/ReturnRequestShow.vue"),
        meta: { title: "Return Request Details" },
      },

      // ── Reports Module ───────────────────
      {
        path: "reports/:module?",
        name: "reports-module",
        component: () => import("@/views/reports/ReportModuleView.vue"),
        meta: { title: "Reports" },
      },

      // ── Outlets Module ───────────────────
      {
        path: "outlets",
        name: "outlets-dashboard",
        component: () => import("@/views/outlets/OutletsDashboard.vue"),
        meta: { title: "Outlets" },
      },
      {
        path: "outlets/types",
        name: "outlets-types",
        component: () => import("@/views/outlets/OutletTypesIndex.vue"),
        meta: { title: "Outlet Types" },
      },
      {
        path: "outlets/types/:id",
        name: "outlets-types-edit",
        component: () => import("@/views/outlets/OutletTypeEdit.vue"),
        meta: { title: "Edit Outlet Type" },
      },
      {
        path: "outlets/list",
        name: "outlets-list",
        component: () => import("@/views/outlets/OutletsIndex.vue"),
        meta: { title: "All Outlets" },
      },
      {
        path: "outlets/branches",
        name: "outlets-branches",
        component: () => import("@/views/outlets/OutletBranchesIndex.vue"),
        meta: { title: "Outlet Branches" },
      },
      {
        path: "outlets/products",
        name: "outlets-all-products",
        component: () => import("@/views/outlets/AllProductsIndex.vue"),
        meta: { title: "All Products" },
      },
      {
        path: "outlets/banners",
        name: "outlets-platform-banners",
        component: () => import("@/views/outlets/PlatformBannersIndex.vue"),
        meta: { title: "Platform Banners" },
      },
      {
        path: "outlets/banners/create",
        name: "outlets-platform-banners-create",
        component: () => import("@/views/outlets/PlatformBannerForm.vue"),
        meta: { title: "Create Banner" },
      },
      {
        path: "outlets/banners/:id/edit",
        name: "outlets-platform-banners-edit",
        component: () => import("@/views/outlets/PlatformBannerForm.vue"),
        meta: { title: "Edit Banner" },
      },
      {
        path: "outlets/inquiries",
        name: "outlets-inquiries-global",
        component: () => import("@/views/outlets/OutletInquiriesIndex.vue"),
        meta: { title: "Inquiries" },
      },
      {
        path: "outlets/messages",
        name: "outlets-messages-global",
        component: () => import("@/views/outlets/OutletMessagesIndex.vue"),
        meta: { title: "Private Conversations" },
      },
      {
        path: "outlets/support",
        name: "outlets-support-dashboard",
        component: () => import("@/views/outlets/OutletsSupportDashboard.vue"),
        meta: { title: "Outlets Support Dashboard" },
      },
      {
        path: "outlets/support/tickets",
        name: "outlets-support-tickets-global",
        component: () => import("@/views/outlets/OutletSupportTicketsIndex.vue"),
        meta: { title: "Outlets Support Tickets" },
      },
      {
        path: "outlets/support/tickets/:id",
        name: "outlets-support-ticket-global",
        component: () => import("@/views/support/TicketDetail.vue"),
        meta: { title: "Ticket Details", context: "outlets" },
      },
      {
        path: "outlets/:parentId/branches/create",
        name: "outlets-branch-create",
        component: () => import("@/views/outlets/BranchCreate.vue"),
        meta: { title: "Create Branch" },
      },
      {
        path: "outlets/create",
        name: "outlets-create",
        component: () => import("@/views/outlets/OutletCreate.vue"),
        meta: { title: "Create Outlet" },
      },
      {
        path: "outlets/:id/edit",
        name: "outlets-edit",
        component: () => import("@/views/outlets/OutletEdit.vue"),
        meta: { title: "Edit Outlet" },
      },
      {
        path: "outlets/:id/show",
        name: "outlets-show",
        component: () => import("@/views/outlets/OutletShow.vue"),
        meta: { title: "Outlet Details" },
      },
      {
        path: "outlets/:id/inquiries",
        name: "outlets-inquiries",
        component: () => import("@/views/outlets/OutletInquiriesIndex.vue"),
        meta: { title: "Outlet Inquiries" },
      },
      {
        path: "outlets/:id/inquiries/:iid",
        name: "outlets-inquiry-show",
        component: () => import("@/views/outlets/OutletInquiryShow.vue"),
        meta: { title: "Inquiry Details" },
      },
      {
        path: "outlets/:id/messages",
        name: "outlets-messages",
        component: () => import("@/views/outlets/OutletMessagesIndex.vue"),
        meta: { title: "Outlet Conversations" },
      },
      {
        path: "outlets/:id/messages/:cid",
        name: "outlets-message-show",
        component: () => import("@/views/outlets/OutletMessageShow.vue"),
        meta: { title: "Conversation Details" },
      },
      {
        path: "outlets/:id/support/tickets",
        name: "outlets-support-tickets",
        component: () => import("@/views/outlets/OutletSupportTicketsIndex.vue"),
        meta: { title: "Outlet Support Tickets" },
      },
      {
        path: "outlets/:id/support/tickets/:tid",
        name: "outlets-support-ticket",
        component: () => import("@/views/support/TicketDetail.vue"),
        meta: { title: "Ticket Details", context: "outlets" },
      },
      {
        path: "outlets/:id/banners",
        name: "outlets-outlet-banners",
        component: () => import("@/views/outlets/OutletBanners.vue"),
        meta: { title: "Outlet Banners" },
      },
      {
        path: "outlets/:id/reviews",
        name: "outlets-reviews",
        component: () => import("@/views/outlets/OutletReviews.vue"),
        meta: { title: "Outlet Reviews" },
      },
      {
        path: "outlets/:id/categories",
        name: "outlets-categories",
        component: () => import("@/views/outlets/OutletCategories.vue"),
        meta: { title: "Outlet Categories" },
      },
      {
        path: "outlets/:id/addons",
        name: "outlets-addons",
        component: () => import("@/views/outlets/OutletAddons.vue"),
        meta: { title: "Outlet Addons" },
      },
      {
        path: "outlets/:id/products",
        name: "outlets-products",
        component: () => import("@/views/outlets/ProductsIndex.vue"),
        meta: { title: "Products" },
      },
      {
        path: "outlets/:id/products/create",
        name: "outlets-products-create",
        component: () => import("@/views/outlets/ProductCreate.vue"),
        meta: { title: "Create Product" },
      },
      {
        path: "outlets/:id/products/:pid/edit",
        name: "outlets-products-edit",
        component: () => import("@/views/outlets/ProductEdit.vue"),
        meta: { title: "Edit Product" },
      },
      {
        path: "outlets/:id/discounts",
        name: "outlets-discounts",
        component: () => import("@/views/discounts/DiscountsIndex.vue"),
        meta: { title: "Outlet Discounts" },
      },
      {
        path: "outlets/:id/discounts/create",
        name: "outlets-discounts-create",
        component: () => import("@/views/discounts/DiscountForm.vue"),
        meta: { title: "Create Outlet Discount" },
      },
      {
        path: "outlets/:id/discounts/:did/edit",
        name: "outlets-discounts-edit",
        component: () => import("@/views/discounts/DiscountForm.vue"),
        meta: { title: "Edit Outlet Discount" },
      },
      {
        path: "outlets/withdrawals",
        name: "outlets-withdrawals",
        component: () => import("@/views/outlets/WithdrawalsIndex.vue"),
        meta: { title: "Merchant Payouts" },
      },
      {
        path: "outlets/payment-accounts",
        name: "outlets-payment-accounts",
        component: () => import("@/views/outlets/PaymentAccountsIndex.vue"),
        meta: { title: "Bank Verification" },
      },

      // ── Discounts ────────────────────────
      {
        path: "discounts",
        name: "discounts-index",
        component: () => import("@/views/discounts/DiscountsIndex.vue"),
        meta: { title: "Discounts" },
      },
      {
        path: "discounts/create",
        name: "discounts-create",
        component: () => import("@/views/discounts/DiscountForm.vue"),
        meta: { title: "Create Discount" },
      },
      {
        path: "discounts/:id/edit",
        name: "discounts-edit",
        component: () => import("@/views/discounts/DiscountForm.vue"),
        meta: { title: "Edit Discount" },
      },

      // ── Coupons ──────────────────────────
      {
        path: "coupons",
        name: "coupons-index",
        component: () => import("@/views/coupons/CouponsIndex.vue"),
        meta: { title: "Coupons" },
      },
      {
        path: "coupons/create",
        name: "coupons-create",
        component: () => import("@/views/coupons/CouponForm.vue"),
        meta: { title: "Create Coupon" },
      },
      {
        path: "coupons/:id/edit",
        name: "coupons-edit",
        component: () => import("@/views/coupons/CouponForm.vue"),
        meta: { title: "Edit Coupon" },
      },

      // ── Platform Campaigns ────────────────
      {
        path: "campaigns",
        name: "campaigns-index",
        component: () => import("@/views/campaigns/CampaignsIndex.vue"),
        meta: { title: "Platform Campaigns" },
      },
      {
        path: "campaigns/create",
        name: "campaigns-create",
        component: () => import("@/views/campaigns/CampaignForm.vue"),
        meta: { title: "Create Campaign" },
      },
      {
        path: "campaigns/:id/edit",
        name: "campaigns-edit",
        component: () => import("@/views/campaigns/CampaignForm.vue"),
        meta: { title: "Edit Campaign" },
      },
      {
        path: "campaigns/:id/show",
        name: "campaigns-show",
        component: () => import("@/views/campaigns/CampaignShow.vue"),
        meta: { title: "Campaign Details" },
      },

      // ── Support Module ────────────────────
      {
        path: "support",
        name: "support-dashboard",
        component: () => import("@/views/support/Dashboard.vue"),
        meta: { title: "Support" },
      },
      {
        path: "support/tickets",
        name: "support-tickets",
        component: () => import("@/views/support/TicketsList.vue"),
        meta: { title: "Support Tickets" },
      },
      {
        path: "support/tickets/:id",
        name: "support-ticket-detail",
        component: () => import("@/views/support/TicketDetail.vue"),
        meta: { title: "Ticket Details" },
      },
      {
        path: "support/types",
        name: "support-types",
        component: () => import("@/views/support/TicketTypes.vue"),
        meta: { title: "Ticket Types" },
      },
    ],
  },
];